import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUserRole, type UserRole } from "../hooks/useUserRole";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  fallback?: string;
  loadingFallback?: React.ReactNode;
  requireOnboarding?: boolean;
}

/**
 * ProtectedRoute Component - Phase 3 Update
 * 
 * Now reads role from Firestore /users/{uid} (not Auth claims)
 * Checks:
 * 1. User is authenticated
 * 2. User has required roles (if specified)
 * 3. User has completed onboarding (if required)
 * 
 * ARCHITECTURE: Single Source of Truth - role from Firestore
 */
export function ProtectedRoute({
  children,
  requiredRoles = ["END_USER", "STAFF", "ADMIN", "KIOSK_ADMIN", "CLUB", "SUPER_ADMIN"],
  fallback = "/wallet",
  loadingFallback = <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>,
  requireOnboarding = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasRole, loading, onboardingCompleted } = useUserRole();
  const location = useLocation();

  if (loading) {
    return <>{loadingFallback}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // NEW: Check onboarding completion (Phase 3 requirement)
  if (requireOnboarding && !onboardingCompleted) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  if (!hasRole(requiredRoles)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
